export const DEFAULT_AGENT_CONVERSATION_PROVIDER = 'aws-bedrock';
export const DEFAULT_AGENT_CONVERSATION_MODEL = 'opus-4.6';

// ---------------------------------------------------------------------------
// Session & top-level state
// ---------------------------------------------------------------------------

export interface AgentSession {
  id: string;
  userId: string;
  studyIds: string[];
  studyNames?: Record<string, string> | null;
  title?: string | null;
  sessionData: SessionData;
  createdAt?: string;
  updatedAt?: string;
  archivedAt?: string | null;
}

export interface SessionData {
  schemaVersion: 1;
  reportState: ReportState;
  conversation: ConversationState;
  navigation: SessionNavigation;
  audit: SessionAudit;
}

// ---------------------------------------------------------------------------
// Report workspace
// ---------------------------------------------------------------------------

export interface ReportState {
  status: 'draft' | 'in_progress' | 'ready';
  studyIds: string[];
  sections: ReportSection[];
  title?: string | null;
  presentationBrief?: ReportPresentationBrief | null;
  activeStudyId?: string | null;
  filters?: ReportWorkspaceFilters | null;
  lastOpenedAt?: string;
}

export interface ReportPresentationBrief {
  objective?: string | null;
  audience?: string | null;
  tone?: string | null;
  coverSubtitle?: string | null;
  coverHighlights: string[];
  executiveSummaryTitle?: string | null;
  executiveSummarySubtitle?: string | null;
  analysisPlanTitle?: string | null;
  analysisPlanSubtitle?: string | null;
  tocTitle?: string | null;
  tocSubtitle?: string | null;
}

export interface ReportWorkspaceFilters {
  labId?: string | null;
  studyIds: string[];
}

// ---------------------------------------------------------------------------
// Section — the only content unit in the report
// ---------------------------------------------------------------------------

export interface ReportSection {
  id: string;
  title: string;
  content?: string | null;
  charts: SectionChart[];
  tables: SectionTable[];
  orderIndex: number;
}

// ---------------------------------------------------------------------------
// Charts — embedded inside sections, each with full provenance
// ---------------------------------------------------------------------------

export type ChartSourceType =
  | 'study_context'
  | 'slide_metadata'
  | 'feature_data'
  | 'marker_qc'
  | 'clinical_correlation'
  | 'session_dataset'
  | 'section_table';

export type ChartType =
  | 'bar'
  | 'barcode'
  | 'pie'
  | 'histogram'
  | 'scatter'
  | 'box'
  | 'violin'
  | 'ecdf'
  | 'density_contour'
  | 'heatmap'
  | 'kaplan_meier'
  | 'forest'
  | 'clustered_heatmap'
  | 'correlation_matrix'
  | 'volcano'
  | 'umap';

export interface PlotlyFigure {
  data: Record<string, unknown>[];
  layout: Record<string, unknown>;
  frames?: Record<string, unknown>[];
}

export interface SectionChart {
  id: string;
  title: string;
  description?: string | null;
  plotlyFigure: PlotlyFigure;
  chartType: ChartType;
  sourceType: ChartSourceType;
  sourceConfig: Record<string, unknown>;
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Tables — embedded tabular data inside sections
// ---------------------------------------------------------------------------

export type TableSourceType =
  | 'study_context'
  | 'cell_distribution'
  | 'cell_density'
  | 'feature_summary'
  | 'clinical_endpoints'
  | 'slide_metadata'
  | 'session_dataset'
  | 'study_cell_rules'
  | 'custom';

// ---------------------------------------------------------------------------
// Session datasets
// ---------------------------------------------------------------------------

export interface SessionDatasetColumn {
  name: string;
  dataType: 'string' | 'number' | 'boolean' | 'date' | 'mixed' | 'empty';
  nullableCount: number;
  distinctCount: number;
  sampleValues: unknown[];
}

import { JobResultDimensionMeta } from './jobResultMetadata';

export interface SessionDatasetMetadata {
  displayName?: string | null;
  description?: string | null;
  kind?: string | null;
  preferredJoinColumn?: string | null;
}

export type SessionDatasetMatchedCaseField = 'case_id' | 'case_label' | 'patient_id' | 'previous_patient_id';

export interface SessionDatasetJoinCandidate {
  datasetColumn: string;
  matchedCaseField: SessionDatasetMatchedCaseField;
  matchedRowCount: number;
  unmatchedRowCount: number;
  ambiguousRowCount: number;
  matchedCaseCount: number;
  matchedCaseIds: number[];
  unmatchedCaseIds: number[];
  score: number;
  isPrimary: boolean;
}

export interface SessionDatasetJoinAnalysis {
  caseCountInScope: number;
  primaryJoinColumn?: string | null;
  primaryMatchedCaseField?: SessionDatasetMatchedCaseField | null;
  candidateMatches: SessionDatasetJoinCandidate[];
}

export interface SessionDataset {
  id: string;
  fileName: string;
  contentType?: string | null;
  sourceType: 'csv' | 'excel';
  uploadedAt: string;
  updatedAt: string;
  rowCount: number;
  columns: SessionDatasetColumn[];
  rows: Record<string, unknown>[];
  metadata: SessionDatasetMetadata;
  joinAnalysis?: SessionDatasetJoinAnalysis | null;
}

export interface UpdateSessionDatasetRequest {
  displayName?: string | null;
  description?: string | null;
  kind?: string | null;
  preferredJoinColumn?: string | null;
}

export interface TableColumn {
  field: string;
  headerName: string;
  type?: 'string' | 'number' | 'boolean' | 'date' | 'dateTime' | 'singleSelect';
  sortable?: boolean;
  filterable?: boolean;
  width?: number;
  description?: string | null;
  align?: 'left' | 'center' | 'right';
  valueOptions?: unknown[];
  unit?: string | null;
  meta?: JobResultDimensionMeta | null;
}

export interface SectionTable {
  id: string;
  title?: string | null;
  description?: string | null;
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  sourceType: TableSourceType;
  sourceConfig: Record<string, unknown>;
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Conversation
// ---------------------------------------------------------------------------

export interface ConversationState {
  provider: typeof DEFAULT_AGENT_CONVERSATION_PROVIDER;
  model: string;
  messages: ConversationMessage[];
  summary?: string | null;
  lastMessageAt?: string;
}

export interface ConversationMessageLiveState {
  statusText?: string | null;
  progress?: AgentStreamProgress | null;
  actions: AgentStreamAction[];
  startedAt?: string | null;
  updatedAt?: string | null;
  completedAt?: string | null;
}

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  createdAt: string;
  updatedAt?: string;
  status?: 'completed' | 'streaming' | 'failed';
  model?: string;
  parentMessageId?: string | null;
  liveState?: ConversationMessageLiveState | null;
  artifacts?: MessageArtifact[];
  citations?: string[];
  tokenTrace?: TokenTrace | null;
  invocations?: Array<Record<string, unknown>>;
  followUpOptions?: FollowUpOption[];
}

export interface MessageArtifact {
  id: string;
  type: 'plotly_chart' | 'table' | 'report_section' | 'reference';
  data: Record<string, unknown>;
  title?: string;
  linkedReportEntityId?: string | null;
}

export interface FollowUpOption {
  label: string;
  prompt: string;
}

// ---------------------------------------------------------------------------
// Navigation & audit
// ---------------------------------------------------------------------------

export interface SessionNavigation {
  openedFrom: PreviousLocation;
  returnToOnClose: PreviousLocation;
}

export interface PreviousLocation {
  pathname: string;
  search: string;
}

export interface SessionAudit {
  createdByUserId: string;
  updatedByUserId: string;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export const createEmptySessionData = ({
  activeStudyId,
  labId,
  openedFrom,
  userId,
}: {
  activeStudyId?: string | null;
  labId?: string | null;
  openedFrom: PreviousLocation;
  userId: string;
}): SessionData => ({
  schemaVersion: 1,
  reportState: {
    status: 'draft',
    studyIds: activeStudyId ? [activeStudyId] : [],
    activeStudyId: activeStudyId || null,
    filters: {
      labId: labId || null,
      studyIds: activeStudyId ? [activeStudyId] : [],
    },
    sections: [],
  },
  conversation: {
    provider: DEFAULT_AGENT_CONVERSATION_PROVIDER,
    model: DEFAULT_AGENT_CONVERSATION_MODEL,
    messages: [],
  },
  navigation: {
    openedFrom,
    returnToOnClose: openedFrom,
  },
  audit: {
    createdByUserId: userId,
    updatedByUserId: userId,
  },
});

// ---------------------------------------------------------------------------
// API request / response contracts
// ---------------------------------------------------------------------------

export interface AgentRequest {
  user_request: string;
  session_id: string;
  origin?: string;
  results_mode?: string;
}

export interface CreateAgentSessionRequest {
  studyIds: string[];
  sessionData: SessionData;
  title?: string | null;
}

export interface UpdateAgentSessionRequest {
  sessionData: SessionData;
  studyIds?: string[] | null;
  title?: string | null;
}

export interface AgentRawResponse {
  message: string;
  invocations: Array<Record<string, unknown>>;
  provider?: string;
  model?: string;
  followUpOptions?: FollowUpOption[];
}

export interface AgentStreamProgress {
  scope: string;
  stage: string;
  progress: number;
  text: string;
}

export interface AgentStreamAction {
  id: string;
  name: string;
  label: string;
  detail?: string | null;
  kind: 'tool' | 'agent' | 'node';
  status: 'running' | 'completed' | 'failed';
  sequence?: number;
  startedAt?: string | null;
  updatedAt?: string | null;
  completedAt?: string | null;
  /** Attached by the frontend when a chart tool completes with plotly data. */
  chartPreview?: {
    id: string;
    title: string;
    chartType: string;
    plotlyFigure: PlotlyFigure;
  } | null;
  /** Attached by the frontend when a table tool completes with table data. */
  tablePreview?: SectionTable | null;
}

// ---------------------------------------------------------------------------
// Token trace (debug)
// ---------------------------------------------------------------------------

export interface TokenTraceEntry {
  step: number;
  agent: string;
  type: 'model_call' | 'tool_call';
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  toolName?: string | null;
  durationMs: number;
  timestamp: number;
}

export interface TokenTrace {
  entries: TokenTraceEntry[];
  planText?: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalCacheReadTokens: number;
  totalCacheWriteTokens: number;
}

// ---------------------------------------------------------------------------
// Report workspace mutation types
// ---------------------------------------------------------------------------

export interface ReportWorkspaceMutationResult {
  session: AgentSession;
  section?: ReportSection | null;
  chart?: SectionChart | null;
  table?: SectionTable | null;
}

/** Create a new section (agent or user). */
export interface CreateReportSectionRequest {
  title: string;
  content?: string | null;
  insertAfterSectionId?: string | null;
}

/** Patch an existing section's text fields. */
export interface UpdateReportSectionRequest {
  title?: string;
  content?: string | null;
}

export interface ReorderReportSectionsRequest {
  orderedSectionIds: string[];
}

// ---------------------------------------------------------------------------
// Add chart to section — chart source configs
// ---------------------------------------------------------------------------

export interface StudyContextChartSource {
  entity?: 'case' | 'slide';
  groupBy: Array<'study' | 'disease' | 'subtype' | 'stain' | 'stain_signature'>;
  metric?: 'case_count' | 'slide_count';
  approvalScope?: 'approved_only' | 'all_non_deleted';
  cohort?: Record<string, unknown> | null;
}

export interface SlideMetadataChartSource {
  field: 'tsm_area_um2' | 'max_resolution' | 'objective_magnification';
  aggregation?: 'histogram' | 'value_counts';
  binCount?: number;
  filters?: Record<string, unknown> | null;
  limit?: number;
}

export interface FeatureDataChartSource {
  featureFamily: 'cell_distribution' | 'cell_density' | 'cell_fraction' | 'od_measurement' | 'spatial_proximity';
  featureNames?: string[];
  groupBy?: string;
  filters?: Record<string, unknown> | null;
}

export interface ClinicalCorrelationChartSource {
  endpoint: 'os' | 'pfs' | 'cb' | 'or';
  featureNames: string[];
  method?: 'kaplan_meier' | 'scatter' | 'box_comparison';
  filters?: Record<string, unknown> | null;
}

export interface SessionDatasetChartSource {
  datasetId: string;
  xField: string;
  yField?: string | null;
  seriesField?: string | null;
  aggregation?: 'count' | 'sum' | 'mean' | 'median' | 'min' | 'max';
  joinColumn?: string | null;
  joinScope?: 'all_rows' | 'matched_rows' | 'unmatched_rows';
  maxCategories?: number;
}

export interface SectionTableChartSource {
  sectionId: string;
  tableId: string;
  xField: string;
  yField?: string | null;
  seriesField?: string | null;
  aggregation?: 'count' | 'sum' | 'mean' | 'median' | 'min' | 'max';
  maxCategories?: number;
}

export interface AddSectionChartRequest {
  sectionId: string;
  chartType: ChartType;
  title: string;
  description?: string | null;
  sourceType: ChartSourceType;
  studyContext?: StudyContextChartSource;
  slideMetadata?: SlideMetadataChartSource;
  featureData?: FeatureDataChartSource;
  clinicalCorrelation?: ClinicalCorrelationChartSource;
  sessionDataset?: SessionDatasetChartSource;
  sectionTable?: SectionTableChartSource;
}

export interface RemoveSectionChartRequest {
  sectionId: string;
  chartId: string;
}

// ---------------------------------------------------------------------------
// Add table to section
// ---------------------------------------------------------------------------

export interface AddSectionTableRequest {
  sectionId: string;
  title?: string | null;
  description?: string | null;
  columns: TableColumn[];
  rows?: Record<string, unknown>[];
  sourceType: TableSourceType;
  sourceConfig?: Record<string, unknown>;
}

export interface RemoveSectionTableRequest {
  sectionId: string;
  tableId: string;
}
